import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { addDays, format, isBefore, isSameDay, startOfDay } from 'date-fns';
import { useInfiniteLeads } from '../../hooks/useInfiniteLeads';
import { createOptimisticItem } from '../../hooks/useOptimisticCRUD';
import { isOptimisticId } from '../../utils/optimisticId';
import { leadsService, projectsService } from '../../services/supabase';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { useConfirmModal } from '../../context/ConfirmModalContext';
import { blockDemoWrite } from '../../services/dataScope';
import { useLeadOptions } from '../../utils/leadOptions';
import { useScrollAndHighlight } from '../../hooks/useScrollAndHighlight';
import { useTableRowNavigation } from '../../hooks/useTableRowNavigation';
import { useGuidedRowMenu } from '../../hooks/useGuidedRowMenu';
import { useCategories } from '../../hooks/useCategories';
import { Lead, LeadStatus, Project } from '../../types';
import { LEAD_STATUS_DISPLAY_ORDER } from '../../utils/statusUtils';
import { LEAD_UPDATED_EVENT } from '../../utils/leadEvents';
import LeadTable from '../../components/leads/LeadTable';
import LeadModal from '../../components/leads/LeadModal';
import FollowUpSection, { type FollowUpTab } from '../../components/leads/FollowUpSection';
import FollowUpModal from '../../components/leads/FollowUpModal';
import PipelineOverview from '../../components/dashboard/PipelineOverview';
import TableSection from '../../components/ui/TableSection';
import TableFilters, { ActiveFilter, FilterFieldDef } from '../../components/ui/TableFilters';
import { matchFilterValue } from '../../utils/filterUtils';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import Card from '../../components/ui/Card';
import TableCardContent from '../../components/ui/TableCardContent';
import { useBulkTableActions } from '../../hooks/useBulkTableActions';
import { useTableInitialLoading } from '../../hooks/useTableInitialLoading';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import { PAGE_STACK, PAGE_GRID_GAP } from '../../styles/pageLayout';
import { exportToCSV, formatDateTimeForExport } from '../../utils/exportUtils';
import ImportModal from '../../components/import/ImportModal';
import { useImport } from '../../hooks/useImport';
import { LEADS_TABLE } from '../../config/tables';
import { getTranslatedCategoryLabel } from '../../utils/categoryTranslations';
import ConfirmModal from '../../components/ui/ConfirmModal';
import Spinner from '../../components/ui/Spinner';
import { Plus } from 'lucide-react';

/** Lead fields filtered client-side (all operators: is, is_not, etc.). API still gets only "is" for status/sector/contact_type/direction. */
const LEAD_CLIENT_FILTER_IDS = new Set(['name', 'contact_value', 'contact_type', 'notes', 'lastActionAt', 'nextActionAt', 'status', 'sector', 'direction']);

function leadMatchesClientFilters(
  lead: Lead,
  activeFilters: ActiveFilter[],
  leadFilterFields: FilterFieldDef[]
): boolean {
  const clientFilters = activeFilters.filter((f) => LEAD_CLIENT_FILTER_IDS.has(f.fieldId));
  if (clientFilters.length === 0) return true;
  return clientFilters.every((f) => {
    const fieldDef = leadFilterFields.find((x) => x.id === f.fieldId);
    if (!fieldDef) return true;
    const getCellValue = (): string | number | null | undefined => {
      if (f.fieldId === 'name') return lead.name ?? '';
      if (f.fieldId === 'contact_value') return lead.contact_value ?? '';
      if (f.fieldId === 'contact_type') return lead.contact_type_id != null ? String(lead.contact_type_id) : '';
      if (f.fieldId === 'notes') return lead.notes ?? '';
      if (f.fieldId === 'status') return lead.status ?? '';
      if (f.fieldId === 'sector') return lead.sector_id != null ? String(lead.sector_id) : '';
      if (f.fieldId === 'direction') return lead.direction ?? '';
      const dateVal = (d: string | Date | null | undefined) =>
        d != null ? (typeof d === 'string' ? d : new Date(d).toISOString().slice(0, 10)) : null;
      if (f.fieldId === 'lastActionAt') return dateVal(lead.lastActionAt);
      if (f.fieldId === 'nextActionAt') return dateVal(lead.nextActionAt);
      return '';
    };
    return matchFilterValue(fieldDef.type, f.operator, f.value ?? '', getCellValue());
  });
}

const Leads: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useI18n();
  const { showToast, showToastWithUndo } = useToast();
  const { confirm } = useConfirmModal();
  const { allStatusOptions, statusOptions, directionOptions, getSectorLabel, allSectorOptions, refreshSectors } = useLeadOptions();
  useScrollAndHighlight();
  // Categories management (loads categories and provides CRUD operations)
  const { categories, loading: categoriesLoading, error: categoriesError, refresh: refreshContactTypes } = useCategories('contact_type');

  // State declarations
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
  const [followUpLead, setFollowUpLead] = useState<Lead | null>(null);
  const { guidedId: guidedMenuLeadId, openGuidedMenu, closeGuidedMenu } = useGuidedRowMenu('data-lead-id');
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const statusFilter = (activeFilters.find((f) => f.fieldId === 'status' && f.operator === 'is')?.value ?? '') as LeadStatus | '';
  const sectorFilter = activeFilters.find((f) => f.fieldId === 'sector' && f.operator === 'is')?.value ?? '';
  const contactTypeFilter = activeFilters.find((f) => f.fieldId === 'contact_type' && f.operator === 'is')?.value ?? '';
  const directionFilter = (activeFilters.find((f) => f.fieldId === 'direction' && f.operator === 'is')?.value ?? '') as 'inbound' | 'outbound' | '';

  // Infinite leads with optimistic CRUD - single source of truth for the table
  const {
    leads: paginatedLeads,
    loadingInitial: paginatedLoading,
    error: paginatedError,
    hasMore,
    loadingMore,
    loadMore,
    refresh: silentRefresh,
    crud: leadsCRUD,  // Optimistic CRUD operations
  } = useInfiniteLeads({
    limit: 30,
    filters: {
      term: deferredSearchTerm,
      status: statusFilter,
      sector: sectorFilter,
      contactType: contactTypeFilter,
      direction: directionFilter,
    },
    onUpdateFailed: () => showToast(t('common.saveFailedRollback'), 'error'),
  });

  const tableServerFilters = useMemo(
    () => ({
      term: deferredSearchTerm,
      status: String(statusFilter || ''),
      sector: String(sectorFilter || ''),
      contactType: String(contactTypeFilter || ''),
      direction: String(directionFilter || ''),
    }),
    [deferredSearchTerm, statusFilter, sectorFilter, contactTypeFilter, directionFilter],
  );

  const hasClientOnlyFilters = useMemo(
    () =>
      activeFilters.some((f) => {
        if (!LEAD_CLIENT_FILTER_IDS.has(f.fieldId)) return false;
        if (f.fieldId === 'status' && f.operator === 'is') return false;
        if (f.fieldId === 'sector' && f.operator === 'is') return false;
        if (f.fieldId === 'contact_type' && f.operator === 'is') return false;
        if (f.fieldId === 'direction' && f.operator === 'is') return false;
        return true;
      }),
    [activeFilters],
  );

  const [pipelineFromServer, setPipelineFromServer] = useState<Record<string, number>>(() => {
    const counts: Record<string, number> = {};
    LEAD_STATUS_DISPLAY_ORDER.forEach((status) => {
      counts[status] = 0;
    });
    return counts;
  });
  const [pipelineInitialLoading, setPipelineInitialLoading] = useState(true);
  const pipelineInitialLoadRef = useRef(true);

  const fetchServerPipelineCounts = useCallback(async () => {
    const isInitial = pipelineInitialLoadRef.current;
    try {
      const counts = await leadsService.getStatusCountsFiltered(tableServerFilters);
      setPipelineFromServer(counts);
    } catch (e) {
      console.error('Error loading pipeline counts:', e);
    } finally {
      if (isInitial) {
        pipelineInitialLoadRef.current = false;
        setPipelineInitialLoading(false);
      }
    }
  }, [tableServerFilters]);

  useEffect(() => {
    if (hasClientOnlyFilters) {
      pipelineInitialLoadRef.current = false;
      setPipelineInitialLoading(false);
      return;
    }
    void fetchServerPipelineCounts();
  }, [hasClientOnlyFilters, fetchServerPipelineCounts]);

  const reloadPipelineCounts = fetchServerPipelineCounts;

  const setStatusFilterFromNav = useCallback((value: LeadStatus | '') => {
    setActiveFilters((prev) => {
      const rest = prev.filter((f) => f.fieldId !== 'status');
      if (!value) return rest;
      return [...rest, { id: `f-status-${Date.now()}`, fieldId: 'status', operator: 'is', value }];
    });
  }, []);

  const handleFollowUpSeeOthers = useCallback((tab: FollowUpTab) => {
    const today = startOfDay(new Date());
    const todayStr = format(today, 'yyyy-MM-dd');
    const tomorrowStr = format(addDays(today, 1), 'yyyy-MM-dd');

    const nextActionFilter =
      tab === 'today'
        ? { id: `f-next-${Date.now()}`, fieldId: 'nextActionAt' as const, operator: 'is' as const, value: todayStr }
        : tab === 'overdue'
          ? { id: `f-next-${Date.now()}`, fieldId: 'nextActionAt' as const, operator: 'before' as const, value: todayStr }
          : { id: `f-next-${Date.now()}`, fieldId: 'nextActionAt' as const, operator: 'before' as const, value: tomorrowStr };

    setActiveFilters((prev) => [...prev.filter((f) => f.fieldId !== 'nextActionAt'), nextActionFilter]);

    window.setTimeout(() => {
      document.getElementById('leads-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const handleFollowUpLeadClick = useCallback((lead: Lead) => {
    setSearchTerm(lead.name);
    setActiveFilters([]);
    window.setTimeout(() => {
      document.getElementById('leads-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  const handlePipelineStageClick = useCallback(
    (status: LeadStatus) => {
      setStatusFilterFromNav(statusFilter === status ? '' : status);
    },
    [statusFilter, setStatusFilterFromNav],
  );

  // Handle URL parameter for lead selection (scroll + highlight)
  useTableRowNavigation<Lead, LeadStatus | ''>({
    paramName: 'lead',
    dataAttribute: 'data-lead-id',
    allItems: paginatedLeads,
    getId: (lead) => lead.id,
    getFilterValue: (lead) => lead.status,
    currentFilter: statusFilter,
    setFilter: setStatusFilterFromNav,
    fetchItem: (id) => leadsService.getById(id),
  });

  // Handle URL parameter for status filter and scroll to table
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const hash = window.location.hash;

    if (statusParam && statusParam !== statusFilter) {
      setStatusFilterFromNav(statusParam as LeadStatus);

      // If we have a hash to scroll to, do it after the filter is applied
      if (hash === '#leads-table') {
        setTimeout(() => {
          const element = document.getElementById('leads-table');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          // Clean the hash from URL after scrolling
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }, 500); // Same delay as for scrolling to a specific lead
      }

      // Clear the URL parameter after applying the filter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('status');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, statusFilter, setSearchParams, setStatusFilterFromNav]);

  const error = categoriesError || paginatedError;

  const showTableInitialLoading = useTableInitialLoading(paginatedLoading, paginatedLeads.length, [
    { loading: categoriesLoading, ready: categories.length > 0 },
  ]);


  // Import hook
  const importHook = useImport({
    table: LEADS_TABLE,
    onImport: async (data: any[]) => {
      if (blockDemoWrite()) {
        return { success: 0, errors: [], warnings: [] };
      }
      // Import leads directly using the service
      const result = await leadsService.importBatch(data) as any;
      
      // Refresh data after successful import
      if (result.success > 0) {
        await silentRefresh();
      }
      
      return {
        success: result.success,
        errors: result.errors,
        warnings: []
      };
    }
  });

  // Follow-ups section: only load leads with next_action_at < tomorrow (overdue + today)
  const [followupLeads, setFollowupLeads] = useState<Lead[]>([]);
  const [followupsLoading, setFollowupsLoading] = useState(true);
  const followupsEnd = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setFollowupsLoading(true);
        const d = await leadsService.getFollowupsBefore(followupsEnd);
        if (!cancelled) setFollowupLeads(d);
      } finally {
        if (!cancelled) setFollowupsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [followupsEnd]);

  const hasFollowups = useMemo(() => {
    const today = startOfDay(new Date());
    return followupLeads.some((lead) => {
      if (!lead.nextActionAt) return false;
      const followUpDate = startOfDay(new Date(lead.nextActionAt as any));
      return isSameDay(followUpDate, today) || isBefore(followUpDate, today);
    });
  }, [followupLeads]);

  // Projects: fetch only what we need for currently loaded leads (avoid projectsService.getAll)
  const [projects, setProjects] = useState<Project[]>([]);
  const projectsKnownLeadIdsRef = useRef<Set<string>>(new Set());
  const projectsLoadSeqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const leadIds = Array.from(
      new Set(
        [...paginatedLeads, ...followupLeads]
          .map((l) => l.id)
          .filter(Boolean)
      )
    );

    const missing = leadIds.filter((id) => !projectsKnownLeadIdsRef.current.has(id));
    if (missing.length === 0) return;

    const load = async () => {
      const seq = ++projectsLoadSeqRef.current;
      try {
        // Chunk to keep IN() queries reasonable
        const chunkSize = 200;
        const chunks: string[][] = [];
        for (let i = 0; i < missing.length; i += chunkSize) {
          chunks.push(missing.slice(i, i + chunkSize));
        }

        const results: Project[] = [];
        for (const chunk of chunks) {
          const part = await projectsService.getByLeadIds(chunk);
          results.push(...part);
        }

        if (cancelled) return;
        if (seq !== projectsLoadSeqRef.current) return;

        // Mark all requested ids as known (even if they have no projects)
        missing.forEach((id) => projectsKnownLeadIdsRef.current.add(id));

        // Merge unique projects by id
        setProjects((prev) => {
          const map = new Map<string, Project>();
          prev.forEach((p) => map.set(p.id, p));
          results.forEach((p) => map.set(p.id, p));
          return Array.from(map.values());
        });
      } catch (e) {
        console.error('Error loading projects for leads:', e);
        // Don't block the page; projects are an enhancement for action menus.
        missing.forEach((id) => projectsKnownLeadIdsRef.current.add(id));
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [paginatedLeads, followupLeads]);

  // Helper: check if a lead qualifies for followups list
  const qualifiesForFollowup = useCallback((lead: Lead): boolean => {
    return !!lead.nextActionAt && new Date(lead.nextActionAt).getTime() < followupsEnd.getTime();
  }, [followupsEnd]);

  // Sync followups with paginatedLeads (handles optimistic create replacements)
  // When an optimistic lead (temp ID) is replaced with real lead, followups are automatically updated
  useEffect(() => {
    // Update followups based on current paginatedLeads
    // This handles the case where optimistic creates are replaced with real leads
    const qualifyingLeads = paginatedLeads.filter(lead => qualifiesForFollowup(lead));
    
    setFollowupLeads((prev) => {
      const updated = new Map<string, Lead>();
      
      // Add all qualifying leads from paginatedLeads (replaces temp IDs with real IDs automatically)
      qualifyingLeads.forEach(lead => {
        updated.set(lead.id, lead);
      });
      
      // Keep existing followups that are not in paginatedLeads but still qualify
      // (handles leads that might be in other sources)
      prev.forEach(followupLead => {
        if (!updated.has(followupLead.id) && qualifiesForFollowup(followupLead)) {
          // Only keep if it's not a temp ID (temp IDs should be replaced by real leads in paginatedLeads)
          if (!followupLead.id.startsWith('temp-')) {
            updated.set(followupLead.id, followupLead);
          }
        }
      });
      
      return Array.from(updated.values());
    });
  }, [paginatedLeads, qualifiesForFollowup]);

  // Helper: add or update lead in followups (deduped by id)
  const upsertFollowupLead = useCallback((lead: Lead) => {
    if (!qualifiesForFollowup(lead)) return;
    setFollowupLeads((prev) => {
      const exists = prev.some((l) => l.id === lead.id);
      if (exists) {
        return prev.map((l) => l.id === lead.id ? lead : l);
      }
      return [lead, ...prev];
    });
  }, [qualifiesForFollowup]);

  // Helper: remove lead from followups by id
  const removeFollowupLead = useCallback((id: string) => {
    setFollowupLeads((prev) => prev.filter((l) => l.id !== id));
  }, []);

  useEffect(() => {
    const onExternalLeadUpdate = (event: Event) => {
      const lead = (event as CustomEvent<Lead>).detail;
      if (!lead?.id) return;
      if (lead.status === 'abandoned' || lead.status === 'rejected' || lead.status === 'completed') {
        removeFollowupLead(lead.id);
      } else if (qualifiesForFollowup(lead)) {
        upsertFollowupLead(lead);
      } else {
        removeFollowupLead(lead.id);
      }
      reloadPipelineCounts();
    };
    window.addEventListener(LEAD_UPDATED_EVENT, onExternalLeadUpdate);
    return () => window.removeEventListener(LEAD_UPDATED_EVENT, onExternalLeadUpdate);
  }, [qualifiesForFollowup, upsertFollowupLead, removeFollowupLead, reloadPipelineCounts]);

  const handleOpenModal = (lead?: Lead) => {
    setEditingLead(lead || null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLead(null);
  };

  // Dashboard empty CTA → /app/leads?create=1
  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    handleOpenModal();
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSaveLead = async (leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingLead) {
      // Update with optimistic UI (instant feedback)
      const updatedLead = { ...editingLead, ...leadData };
      leadsCRUD.update(editingLead.id, updatedLead, { previousItem: editingLead });
      
      // Update followups explicitly
      if (qualifiesForFollowup(updatedLead as Lead)) {
        upsertFollowupLead(updatedLead as Lead);
      } else {
        removeFollowupLead(editingLead.id);
      }
    } else {
      // Create with optimistic UI (instant feedback)
      const optimisticLead = createOptimisticItem<Lead>(leadData);
      leadsCRUD.create(leadData);
      
      // Update followups immediately with optimistic lead
      // When server responds, the temp lead will be replaced with real lead in paginatedLeads
      // The useEffect below will sync followups automatically
      if (qualifiesForFollowup(optimisticLead)) {
        upsertFollowupLead(optimisticLead);
      }
    }
    // Refresh category lists so new sectors/contact types created in the modal appear in table/filters
    refreshContactTypes();
    refreshSectors();
    reloadPipelineCounts();
  };

  const handleDeleteLead = (id: string) => {
    // Fire-and-forget: ActionIconButton shows a spinner while awaiting onClick.
    // Confirm modal can stay open for a while — don't keep the row loader spinning.
    void (async () => {
      const leadToDelete = paginatedLeads.find(l => l.id === id) || followupLeads.find(l => l.id === id);
      if (!leadToDelete) return;

      // Count linked projects (local cache, or fetch if this lead isn't loaded yet)
      let linkedProjects = projects.filter((p) => p.leadId === id);
      if (!projectsKnownLeadIdsRef.current.has(id)) {
        try {
          const fetched = await projectsService.getByLeadIds([id]);
          projectsKnownLeadIdsRef.current.add(id);
          if (fetched.length > 0) {
            linkedProjects = fetched;
            setProjects((prev) => {
              const map = new Map(prev.map((p) => [p.id, p]));
              fetched.forEach((p) => map.set(p.id, p));
              return Array.from(map.values());
            });
          }
        } catch {
          // Fall through with local count
        }
      }

      if (linkedProjects.length > 0) {
        const confirmed = await confirm({
          title: t('leads.confirmDeleteWithProjectsTitle'),
          message: t('leads.confirmDeleteWithProjects', { count: linkedProjects.length }),
          confirmText: t('common.delete'),
          variant: 'danger',
        });
        if (!confirmed) return;
      }

      // Store the lead data for undo
      const leadData = { ...leadToDelete };

      // Delete with optimistic UI (instant feedback)
      leadsCRUD.remove(id);
      removeFollowupLead(id);
      if (linkedProjects.length > 0) {
        const removedIds = new Set(linkedProjects.map((p) => p.id));
        setProjects((prev) => prev.filter((p) => !removedIds.has(p.id)));
      }
      reloadPipelineCounts();

      // Show toast with undo
      showToastWithUndo(
        t('leads.deleted', { name: leadToDelete.name }),
        () => {
          // Undo: recreate the lead with optimistic UI
          // (cascaded projects / files are not restored)
          const optimisticLead = createOptimisticItem<Lead>({
            name: leadData.name,
            contact_type_id: leadData.contact_type_id,
            contact_value: leadData.contact_value,
            sector_id: leadData.sector_id,
            status: leadData.status,
            notes: leadData.notes,
            direction: leadData.direction,
            lastActionAt: leadData.lastActionAt,
            nextActionAt: leadData.nextActionAt,
            followupCount: leadData.followupCount,
          });
          leadsCRUD.create({
            name: leadData.name,
            contact_type_id: leadData.contact_type_id,
            contact_value: leadData.contact_value,
            sector_id: leadData.sector_id,
            status: leadData.status,
            notes: leadData.notes,
            direction: leadData.direction,
            lastActionAt: leadData.lastActionAt,
            nextActionAt: leadData.nextActionAt,
            followupCount: leadData.followupCount,
          });
          // Update followups immediately with optimistic lead
          if (qualifiesForFollowup(optimisticLead)) {
            upsertFollowupLead(optimisticLead);
          }
        },
        'error'
      );
    })();
  };

  const handleCreateProject = (lead: Lead) => {
    closeGuidedMenu();
    if (isOptimisticId(lead.id)) {
      showToast(t('projects.leadStillSaving'), 'error');
      return;
    }
    navigate(`/app/projects?create=1&lead=${encodeURIComponent(lead.id)}`);
  };

  // Check if a lead has an associated project
  const getLeadProject = (leadId: string) => {
    return projects.find((project: any) => project.leadId === leadId);
  };

  const handleViewProject = (leadOrProject: Lead | any) => {
    let project: any;
    // If it's a Lead (has no leadId property, only id), get the associated project
    if (!('leadId' in leadOrProject) && 'id' in leadOrProject) {
      // It's a Lead, get the associated project
      project = getLeadProject(leadOrProject.id);
    } else {
      // If it's already a Project (has both id and leadId)
      project = leadOrProject;
    }
    if (project && project.id) {
      // Navigate to Projects page with project selected
      navigate(`/app/projects?project=${project.id}`);
    }
  };

  const handleReschedule = (lead: Lead) => {
    setFollowUpLead(lead);
    setIsFollowUpModalOpen(true);
  };

  const handleMoveToNextStatus = async (lead: Lead) => {
    const { getNextLeadStatus } = await import('../../utils/statusUtils');
    const nextStatus = getNextLeadStatus(lead.status);
    if (nextStatus) {
      const updatedLead = {
        ...lead,
        status: nextStatus as LeadStatus,
        lastActionAt: new Date(),
        ...(nextStatus === 'abandoned' && { nextActionAt: null })
      };
      
      // Update with optimistic UI
      leadsCRUD.update(lead.id, updatedLead, { previousItem: lead });
      
      // Update followups explicitly
      if (qualifiesForFollowup(updatedLead)) {
        upsertFollowupLead(updatedLead);
      } else {
        removeFollowupLead(lead.id);
      }
      reloadPipelineCounts();
    }
  };

  const handleFollowUpSave = (lead: Lead, newDate: Date) => {
    const updatedLead = { ...lead, nextActionAt: newDate };
    
    // Update with optimistic UI
    leadsCRUD.update(lead.id, updatedLead, { previousItem: lead });
    
    // Update followups explicitly
    if (qualifiesForFollowup(updatedLead)) {
      upsertFollowupLead(updatedLead);
    } else {
      removeFollowupLead(lead.id);
    }
  };

  const handleMarkAsAbandoned = (lead: Lead) => {
    const updatedLead = {
      ...lead,
      status: 'abandoned' as LeadStatus,
      nextActionAt: null,
      lastActionAt: new Date(),
    };
    
    // Update with optimistic UI
    leadsCRUD.update(lead.id, updatedLead, { previousItem: lead });
    
    // Remove from followups (abandoned leads have no nextActionAt)
    removeFollowupLead(lead.id);
    reloadPipelineCounts();
  };

  const handleMarkAsRejected = (lead: Lead) => {
    const updatedLead = {
      ...lead,
      status: 'rejected' as LeadStatus,
      nextActionAt: null,
      lastActionAt: new Date(),
    };
    leadsCRUD.update(lead.id, updatedLead, { previousItem: lead });
    removeFollowupLead(lead.id);
    reloadPipelineCounts();
  };

  const handleMessage = (lead: Lead) => {
    navigate(`/app/messages?lead=${lead.id}`);
  };

  const leadMenuActions = {
    onContact: handleMessage,
    onMessage: handleMessage,
    onReschedule: handleReschedule,
    onCreateProject: handleCreateProject,
    onViewProject: handleViewProject,
    onMarkAsAbandoned: handleMarkAsAbandoned,
    onMarkAsRejected: handleMarkAsRejected,
    onMoveToNextStatus: handleMoveToNextStatus,
  };

  const handleExport = async () => {
    try {
      // Always fetch ALL leads directly from service to ensure we get everything
      const allLeadsFromService = await leadsService.getAll();

      if (allLeadsFromService.length === 0) {
        showToast(t('leads.noLeadsToExport'), 'warning');
        return;
      }

      // Apply same filters as table (search + all active filters with all operators)
      const searchLower = searchTerm.trim().toLowerCase();
      const filteredLeadsToExport = allLeadsFromService.filter((lead) => {
        if (searchLower && !(lead.name ?? '').toLowerCase().includes(searchLower)) return false;
        return leadMatchesClientFilters(lead, activeFilters, leadFilterFields);
      });

      if (filteredLeadsToExport.length === 0) {
        showToast(t('leads.noLeadsToExport'), 'warning');
        return;
      }

      // Same order as in app: last_action_at desc, then updated_at, then created_at (deterministic for same-day)
      const sortedForExport = [...filteredLeadsToExport].sort((a, b) => {
        const ta = a.lastActionAt ? new Date(a.lastActionAt).getTime() : -1;
        const tb = b.lastActionAt ? new Date(b.lastActionAt).getTime() : -1;
        if (ta !== tb) return tb - ta;
        const ua = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const ub = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        if (ua !== ub) return ub - ua;
        return (
          (b.createdAt ? new Date(b.createdAt).getTime() : 0) -
          (a.createdAt ? new Date(a.createdAt).getTime() : 0)
        );
      });

      // Headers = app labels (i18n), same order as table: Lead, Contact, Dir, Secteur, Statut, Dernière action, Prochaine action, Notes, Créé le
      const exportHeaders = [
        t('leads.singular'),
        t('leads.contactType'),
        t('leads.contactValue'),
        t('leads.source'),
        t('leads.sector'),
        t('leads.status'),
        t('leads.lastAction'),
        t('leads.nextAction'),
        t('leads.notes'),
        t('leads.createdAt')
      ];

      const exportData = sortedForExport.map(lead => ({
        [t('leads.singular')]: lead.name,
        [t('leads.contactType')]: lead.contact_type || '',
        [t('leads.contactValue')]: lead.contact_value || '',
        [t('leads.source')]: lead.direction || '',
        [t('leads.sector')]: getSectorLabel(lead.sector_id) || '',
        [t('leads.status')]: lead.status,
        [t('leads.lastAction')]: lead.lastActionAt != null ? formatDateTimeForExport(lead.lastActionAt) : '',
        [t('leads.nextAction')]: lead.nextActionAt != null ? formatDateTimeForExport(lead.nextActionAt) : '',
        [t('leads.notes')]: lead.notes || '',
        [t('leads.createdAt')]: formatDateTimeForExport(lead.createdAt)
      }));

      exportToCSV(exportData, `leads-${new Date().toISOString().split('T')[0]}`, exportHeaders);
      showToast(t('common.exportSuccess', { count: exportData.length }) || `${exportData.length} leads exportés`, 'success');
    } catch (error) {
      console.error('Error exporting leads:', error);
      showToast(t('leads.exportError') || 'Erreur lors de l\'export des leads', 'error');
    }
  };

  // Sector options from categories (type=sector): global + user-created
  const sectorOptions = useMemo(() => {
    const [allOpt, ...rest] = allSectorOptions;
    rest.sort((a, b) => a.label.localeCompare(b.label));
    return [allOpt, ...rest];
  }, [allSectorOptions]);

  const directionFilterOptions = useMemo(
    () => [
      { value: '', label: t('leads.allDirections') },
      ...directionOptions.filter(opt => opt.value !== ''),
    ],
    [t, directionOptions]
  );

  const contactTypeFilterOptions = useMemo(
    () => [
      { value: '', label: t('leads.allContactTypes') },
      ...categories.map((cat) => ({
        value: String(cat.id),
        label: getTranslatedCategoryLabel(cat, t, language),
      })),
    ],
    [categories, t, language]
  );

  // Ordre = colonnes de la table (gauche → droite) puis champs hors colonne
  const leadFilterFields: FilterFieldDef[] = useMemo(
    () => [
      { id: 'name', type: 'text', label: t('leads.singular'), placeholder: t('common.search') },
      { id: 'contact_type', type: 'select', label: t('leads.contactType'), options: contactTypeFilterOptions },
      { id: 'contact_value', type: 'text', label: t('leads.contactValue'), placeholder: t('common.search') },
      { id: 'direction', type: 'select', label: t('leads.direction'), options: directionFilterOptions },
      { id: 'sector', type: 'select', label: t('leads.sector'), options: sectorOptions },
      { id: 'status', type: 'select', label: t('leads.status'), options: allStatusOptions },
      { id: 'lastActionAt', type: 'date', label: t('leads.lastAction'), placeholder: '' },
      { id: 'nextActionAt', type: 'date', label: t('leads.nextAction'), placeholder: '' },
      { id: 'notes', type: 'text', label: t('leads.notes'), placeholder: t('common.search') },
    ],
    [t, allStatusOptions, sectorOptions, directionFilterOptions, contactTypeFilterOptions]
  );

  // Client-side filters (all operators) via leadMatchesClientFilters; API still gets only "is" for status/sector/contact_type/direction.
  const displayedLeads = useMemo(() => {
    const clientFilters = activeFilters.filter((f) => LEAD_CLIENT_FILTER_IDS.has(f.fieldId));
    if (clientFilters.length === 0) return paginatedLeads;
    return paginatedLeads.filter((lead) => leadMatchesClientFilters(lead, activeFilters, leadFilterFields));
  }, [paginatedLeads, activeFilters, leadFilterFields]);

  const pipelineFromTable = useMemo(() => {
    const counts: Record<string, number> = {};
    LEAD_STATUS_DISPLAY_ORDER.forEach((status) => {
      counts[status] = 0;
    });
    displayedLeads.forEach((lead) => {
      if (lead.status) {
        counts[lead.status] = (counts[lead.status] || 0) + 1;
      }
    });
    return counts;
  }, [displayedLeads]);

  const pipelineByStatus = hasClientOnlyFilters ? pipelineFromTable : pipelineFromServer;

  const {
    tableProps: bulkTableProps,
    bulkBarProps,
    bulkDeleteModal,
    selectedItems: selectedLeads,
  } = useBulkTableActions<Lead>({
    items: displayedLeads,
    onBulkDeleteConfirm: (ids) => {
      leadsCRUD.bulkRemove(ids);
      ids.forEach(removeFollowupLead);
      const idSet = new Set(ids);
      setProjects((prev) => prev.filter((p) => !p.leadId || !idSet.has(p.leadId)));
      reloadPipelineCounts();
    },
  });

  const bulkDeleteLinkedProjectCount = useMemo(() => {
    if (!bulkDeleteModal.isOpen || selectedLeads.length === 0) return 0;
    const leadIds = new Set(selectedLeads.map((l) => l.id));
    return projects.filter((p) => p.leadId && leadIds.has(p.leadId)).length;
  }, [bulkDeleteModal.isOpen, selectedLeads, projects]);

  const contactTypeOptions = useMemo(
    () =>
      categories.map((cat) => ({
        value: String(cat.id),
        label: getTranslatedCategoryLabel(cat, t, language),
      })),
    [categories, t, language]
  );

  const handleBulkStatusUpdate = (ids: string[], newStatus: string) => {
    const selectedById = new Map(selectedLeads.map((l) => [l.id, l]));
    const baseLeads = ids
      .map((id) => selectedById.get(id) ?? paginatedLeads.find((l) => l.id === id))
      .filter((lead): lead is Lead => lead !== undefined);

    if (baseLeads.length !== ids.length) {
      showToast(t('common.bulkSelectionStale'), 'warning');
    }

    const updatedLeads = baseLeads.map((lead) => ({
      ...lead,
      status: newStatus as LeadStatus,
      lastActionAt: new Date(),
      ...(newStatus === 'abandoned' && { nextActionAt: null }),
    }));

    leadsCRUD.bulkUpdate(updatedLeads);
    updatedLeads.forEach((lead) => {
      if (qualifiesForFollowup(lead)) {
        upsertFollowupLead(lead);
      } else {
        removeFollowupLead(lead.id);
      }
    });
    reloadPipelineCounts();
  };

  const handleBulkContactTypeUpdate = (ids: string[], newTypeId: string) => {
    const typeId = parseInt(newTypeId, 10);
    if (!Number.isFinite(typeId)) return;

    const selectedById = new Map(selectedLeads.map((l) => [l.id, l]));
    const baseLeads = ids
      .map((id) => selectedById.get(id) ?? paginatedLeads.find((l) => l.id === id))
      .filter((lead): lead is Lead => lead !== undefined);

    if (baseLeads.length !== ids.length) {
      showToast(t('common.bulkSelectionStale'), 'warning');
    }

    const updatedLeads = baseLeads.map((lead) => ({
      ...lead,
      contact_type_id: typeId,
    }));

    leadsCRUD.bulkUpdate(updatedLeads);
    showToast(t('leads.bulkContactTypeUpdated', { count: updatedLeads.length }), 'success');
  };

  return (
    <div className={`w-full ${PAGE_STACK}`}>
      {/* Header */}
      <PageHeader
        title={t('leads.title')}
        description={t('leads.manageLeads')}
        actionsConfig={{
          newButtonKey: 'leads.createNew',
          onCreateNew: () => handleOpenModal(),
          onExport: handleExport,
          onImport: importHook.openImportModal,
          hideNewOnMobile: true,
        }}
      />

      {/* Stats removed on non-overview pages */}

      {/* Follow-ups + pipeline (pipeline hidden below xl; layout stays stable while data refreshes) */}
      <div
        className={`grid grid-cols-1 ${PAGE_GRID_GAP} ${
          hasFollowups ? 'xl:grid-cols-2 xl:items-stretch' : 'items-start'
        }`}
      >
        {hasFollowups || followupsLoading ? (
          followupsLoading ? (
            <Card className="h-full xl:!pb-4">
              <div className="flex h-full min-h-0 flex-1 items-center justify-center py-8">
                <Spinner size="md" label={t('common.loading')} />
              </div>
            </Card>
          ) : (
            <div className="h-full min-h-0">
              <FollowUpSection
                leads={followupLeads}
                categories={categories}
                leadMenuActions={leadMenuActions}
                projects={projects}
                onEdit={handleOpenModal}
                onDelete={handleDeleteLead}
                onSeeOthers={handleFollowUpSeeOthers}
                onLeadClick={handleFollowUpLeadClick}
              />
            </div>
          )
        ) : null}

        <div className="hidden h-full min-h-0 xl:block">
          {pipelineInitialLoading ? (
            <Card className="h-full xl:!pb-4">
              <div className="flex h-full min-h-0 flex-1 items-center justify-center py-8">
                <Spinner size="md" label={t('common.loading')} />
              </div>
            </Card>
          ) : (
            <PipelineOverview
              leadsByStatus={pipelineByStatus as Record<LeadStatus, number>}
              conversionRate={0}
              showTitle={false}
              showConversionRate={false}
              showTitleIcon={false}
              activeStatus={statusFilter}
              onStageClick={handlePipelineStageClick}
              className="h-full xl:!pb-4"
              minHeightClass="min-h-0"
            />
          )}
        </div>
      </div>

      {/* Table Section */}
      <TableSection
        actionsConfig={{}}
        filters={
          <TableFilters
            searchConfig={{
              value: searchTerm,
              onChange: setSearchTerm,
              placeholder: t('leads.searchLead'),
            }}
            filterFields={leadFilterFields}
            activeFilters={activeFilters}
            onChange={setActiveFilters}
            mobilePrimaryAction={
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleOpenModal()}
                icon={<Plus className="w-4 h-4" />}
              >
                {t('common.new')}
              </Button>
            }
          />
        }
      >
        {/* Bulk actions bar - selection count, change status, delete */}
        <BulkActionsBar
          {...bulkBarProps}
          statusOptions={statusOptions}
          onUpdateStatus={handleBulkStatusUpdate}
          contactTypeOptions={contactTypeOptions}
          onUpdateContactType={handleBulkContactTypeUpdate}
        />
        <Card padding="none">
          <div id="leads-table">
            <TableCardContent error={error} showInitialLoading={showTableInitialLoading}>
              <LeadTable
                leads={displayedLeads}
                categories={categories}
                onEdit={handleOpenModal}
                onDelete={handleDeleteLead}
                leadMenuActions={leadMenuActions}
                getLeadProject={getLeadProject}
                projects={projects}
                onBulkDelete={bulkTableProps.onBulkDelete}
                onRegisterToggleAll={bulkTableProps.onRegisterToggleAll}
                onRegisterBulkDelete={bulkTableProps.onRegisterBulkDelete}
                onRegisterClearSelection={bulkTableProps.onRegisterClearSelection}
                onSelectedCountChange={bulkTableProps.onSelectedCountChange}
                onIsAllSelectedChange={bulkTableProps.onIsAllSelectedChange}
                onSelectedLeadsChange={bulkTableProps.onSelectedItemsChange as (leads: Lead[]) => void}
                hideBulkActionsBar
                hasMore={hasMore}
                loadingMore={loadingMore}
                onLoadMore={loadMore}
                isFilteredEmpty={activeFilters.length > 0 || deferredSearchTerm.trim().length > 0}
                onCreate={() => handleOpenModal()}
                guidedMenuLeadId={guidedMenuLeadId}
                onGuidedMenuClose={closeGuidedMenu}
              />
            </TableCardContent>
          </div>
        </Card>
      </TableSection>

      {/* Modals */}
      <ConfirmModal
        isOpen={bulkDeleteModal.isOpen}
        onClose={bulkDeleteModal.onClose}
        onConfirm={bulkDeleteModal.onConfirm}
        title={t('common.confirm')}
        message={
          bulkDeleteLinkedProjectCount > 0
            ? t('leads.confirmBulkDeleteWithProjects', {
                count: bulkDeleteModal.pendingCount,
                projectCount: bulkDeleteLinkedProjectCount,
              })
            : t('leads.confirmBulkDelete', { count: bulkDeleteModal.pendingCount })
        }
        variant="danger"
      />

      <LeadModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveLead}
        lead={editingLead}
        onGuideCreateProject={(lead) => openGuidedMenu(lead.id)}
        onLeadsAffected={() => {
          silentRefresh();
          refreshContactTypes();
          refreshSectors();
        }}
      />

      {/* Follow-up Modal */}
      <FollowUpModal
        isOpen={isFollowUpModalOpen}
        onClose={() => setIsFollowUpModalOpen(false)}
        onSave={handleFollowUpSave}
        lead={followUpLead}
      />

      {/* Import Modal */}
      <ImportModal
        isOpen={importHook.isImportModalOpen}
        onClose={importHook.closeImportModal}
        onImport={importHook.handleImport}
        table={importHook.table}
      />

    </div>
  );
};

export default Leads;
